import type { BootstrapData, Dosha, DoshaProfile, Question } from "./types";

const PROFILE_KEY = "ayurvedon.profile.v1";

function clearLegacyProfileCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = "ayurvedon_profile=; Max-Age=0; Path=/; SameSite=Lax";
}

export async function loadBootstrap(signal?: AbortSignal): Promise<BootstrapData> {
  const response = await fetch("/api/bootstrap?locale=de", { signal });
  if (!response.ok) throw new Error(`Inhalte konnten nicht geladen werden (${response.status})`);
  return response.json() as Promise<BootstrapData>;
}

export function calculateProfile(
  questions: Question[],
  answers: Record<string, number>,
  questionnaireVersion: string,
): DoshaProfile {
  const scores: Record<Dosha, number> = { vata: 0, pitta: 0, kapha: 0 };
  questions.forEach((question) => {
    const option = question.options[answers[question.id]];
    if (!option) return;
    (Object.keys(scores) as Dosha[]).forEach((dosha) => {
      scores[dosha] += option.scores[dosha] ?? 0;
    });
  });

  const ordered = (Object.entries(scores) as [Dosha, number][]).sort((a, b) => b[1] - a[1]);
  const secondary = ordered[0][1] - ordered[1][1] <= 3 ? ordered[1][0] : null;
  return {
    schema: 1,
    scores,
    primary: ordered[0][0],
    secondary,
    completedAt: new Date().toISOString(),
    questionnaireVersion,
  };
}

export function saveProfile(profile: DoshaProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  clearLegacyProfileCookie();
}

export function readProfile(): DoshaProfile | null {
  clearLegacyProfileCookie();
  try {
    const value = localStorage.getItem(PROFILE_KEY);
    return value ? (JSON.parse(value) as DoshaProfile) : null;
  } catch {
    return null;
  }
}

export function clearProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
  clearLegacyProfileCookie();
}

export const DOSHA_META: Record<Dosha, { label: string; essence: string; color: string }> = {
  vata: { label: "Vata", essence: "Bewegung · Leichtigkeit · Kreativität", color: "#6b718a" },
  pitta: { label: "Pitta", essence: "Wandlung · Klarheit · Intensität", color: "#c46b3c" },
  kapha: { label: "Kapha", essence: "Struktur · Ruhe · Beständigkeit", color: "#4f7b62" },
};

export function formatDoshas(doshas: Dosha[]): string {
  return doshas.map((dosha) => DOSHA_META[dosha].label).join(" · ");
}
